import type { RefObject } from 'react';
import React from 'react';

import { InputAdornment } from '@mui/material';
import clsx from 'clsx';
import { AlineInput } from 'src/AlineInput';
import { SmallCloseIconButton } from 'src/components/generic/SmallCloseIconButton';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { makeStyles } from 'tss-react/mui';

type StyleProps = {
  minWidth?: number | string;
};

const useStyles = makeStyles<StyleProps>()((theme, { minWidth }) => ({
  inputWrapper: {
    '&&': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 8,
    },
  },
  root: {
    minWidth: minWidth ?? 320,
    padding: theme.spacing(0, 1),
    width: '100%',
  },
}));

interface SearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'color' | 'size'
> {
  search: string;
  setSearch: (search: string) => void;
  inputClassName?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  minWidth?: number | string;
}

export function SearchInput({
  autoFocus = true,
  inputClassName,
  inputRef,
  minWidth,
  search,
  setSearch,
  ...props
}: SearchInputProps): JSX.Element {
  const { classes } = useStyles({ minWidth });

  function searchChangeHandler(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearch(event.target.value);
  }

  return (
    <AlineInput
      autoFocus={autoFocus}
      classes={{ root: classes.root }}
      endAdornment={
        Boolean(search.length) && (
          <InputAdornment position="end">
            <SmallCloseIconButton
              onMouseDown={(e: React.MouseEvent): void => {
                e.preventDefault();
                e.stopPropagation();
                setSearch('');
              }}
            />
          </InputAdornment>
        )
      }
      fullWidth={false}
      inputClassName={clsx(classes.inputWrapper, inputClassName)}
      inputRef={inputRef}
      startAdornment={
        <InputAdornment position="start">
          <MagnifyingGlassIcon />
        </InputAdornment>
      }
      value={search}
      onChange={searchChangeHandler}
      {...props}
    />
  );
}
